/**
 * Interaction Handlers — INTERACTION_REQUIRED (Phase 9: legacy event listeners removed)
 */

import type { Socket } from "socket.io-client";
import { EVENTS } from "@/constants/events"; // EVENTS.INTERACTION_REQUIRED still used
import { useTaskStore } from "../taskStore";
import {
  DEFAULT_AGENT,
  pickHandoffFields,
  type ToolUseBlock,
  type QuestionBlock,
  type PendingInteraction,
  type PendingCustomInteraction,
  type TaskState,
} from "../types";
import type {
  InteractionRequest,
  SubagentId,
  SubagentPathSegment,
} from "@bash/shared";
import { resolveIntegrationDisplayName } from "@/lib/composio-tools";
import { resolveInteractionApi } from "@/lib/api/interactions";
import { enqueueMainPending } from "../pendingQueue";

export function registerInteractionHandlers(socket: Socket): () => void {
  const taskStore = useTaskStore;

  /**
   * INTERACTION_REQUIRED (Input Station) — build PendingInteraction directly
   * from the typed InteractionRequest (one _mutateTasks call per kind).
   *
   * Phase 9: legacy PENDING_QUESTION / PENDING_APPROVAL / PERMISSION_REQUEST /
   * CONNECTION_REQUIRED listeners removed. INTERACTION_REQUIRED is the sole
   * incoming interaction signal.
   */
  const handleInteractionRequired = (data: {
    taskId: string;
    request: InteractionRequest;
    // T9 — OPTIONAL subagent identity, ridden onto the wire alongside `request`
    // by the sandbox sink (harness-sink.ts). Absent ⇒ a main-agent pause.
    subagentId?: SubagentId;
    subagentPath?: SubagentPathSegment[];
  }) => {
    const { taskId, request, subagentId, subagentPath } = data;
    if (!taskId || !request) return;
    const agentSlug = DEFAULT_AGENT;
    const base = {
      requestId: request.id,
      toolUseId: request.toolUseId ?? request.id,
      agentSlug,
      taskId,
      interactionId: request.id,
      // Stamp the subagent identity so the store + DelegationCard lane can route
      // and attribute this pause. Conditional-spread keeps a main-agent base
      // byte-identical to today (keys omitted entirely).
      ...(subagentId !== undefined && { subagentId }),
      ...(subagentPath !== undefined && { subagentPath }),
    };

    /**
     * Route a freshly-built pending interaction to its lane.
     *   - subagent pause ⇒ `pendingByLane[subagentId]`; the global InputStation
     *     (which reads the main `pendingInteraction`) stays in text mode, so only
     *     the matching DelegationCard lane blocks while siblings keep streaming.
     *     Phase is left as the live turn's (processing/waiting).
     *   - main-agent pause ⇒ `pendingInteraction` + `phase='idle'` (today's UX).
     */
    const stampPending = (task: TaskState, pending: PendingInteraction) => {
      if (subagentId !== undefined) {
        (task.pendingByLane ??= {})[subagentId] = pending;
      } else {
        // A parallel tool batch can mint SEVERAL main-agent pauses in the same
        // instant — queue them FIFO (`pendingInteraction` = visible head,
        // `pendingQueue` = tail) instead of last-write-wins overwriting, which
        // hid all but the last card and left the turn looking stuck after the
        // user approved it (incident a671cd77).
        enqueueMainPending(task, pending);
        task.phase = "idle";
      }
    };

    switch (request.kind) {
      case "ask_question": {
        const questions = request.questions as QuestionBlock["questions"];
        const firstQ = questions?.[0]?.question || "";
        const isPreview =
          firstQ.startsWith("<!--agent-preview:") ||
          firstQ.startsWith("<!--skill-preview:") ||
          firstQ.startsWith("<!--routine-preview:");

        // Single source of truth: the SDK's tool_use block. We stamp the
        // answered state directly onto the existing AskUserQuestion tool_use
        // (matched by toolUseId) — no parallel `question` content block, no
        // dedup hacks, no race conditions when streamingMessage flips. The
        // `ToolUseBlock` AskUserQuestion fallback renders the answered card
        // from `block.userAnswers`. Survives MESSAGE_SYNC because tool_use
        // blocks are persisted server-side; userAnswers is reconciled by
        // syncHandlers.
        if (isPreview) {
          const previewAnswers = { [firstQ]: "Create it" };
          taskStore.getState()._mutateTasks((tasks) => {
            const task = tasks.get(taskId);
            if (!task) return;
            const stamp = (
              content: Array<{
                type: string;
                toolUseId?: string;
                toolName?: string;
                userAnswers?: Record<string, string>;
              }>
            ) => {
              for (const block of content) {
                if (
                  block.type === "tool_use" &&
                  (block as ToolUseBlock).toolUseId === base.toolUseId
                ) {
                  (block as ToolUseBlock).userAnswers = previewAnswers;
                  return true;
                }
              }
              return false;
            };
            if (
              task.streamingMessage &&
              stamp(task.streamingMessage.content as never)
            )
              return;
            for (const m of task.messages) {
              if (stamp(m.content as never)) return;
            }
          });
          resolveInteractionApi(request.id, {
            behavior: "allow",
            updatedInput: { answers: previewAnswers },
          }).catch((err) =>
            console.warn(
              "[interactionHandlers] auto-approve resolve failed:",
              err
            )
          );
          return;
        }

        // Vanilla AskUserQuestion: input area drives the interaction via
        // `pendingInteraction`. ToolUseBlock fallback renders the answered ✓
        // card after the user answers (stamped by _respondToInteraction in
        // taskStore). No in-stream block to inject.
        // Browser handoff (spec 2026-09-02): the row may say what the ask is
        // and where the person acts; keep those words or the story renders a
        // handoff as a text box (the disease the spec names).
        const handoff = pickHandoffFields(
          request as unknown as Record<string, unknown>
        );
        taskStore.getState()._mutateTasks((tasks) => {
          const task = tasks.get(taskId);
          if (!task) return;
          stampPending(task, {
            ...base,
            type: "question",
            questions,
            ...handoff,
          });
        });
        return;
      }

      case "composio_auth": {
        const authToolkit = request.toolkit.toLowerCase();
        // The brand, resolved once at the mint site so every consumer (pending
        // card, stream AuthCard displayText/integrationName) agrees: our
        // catalog first, the provided name (minus the "MCP" transport word)
        // for a toolkit we do not know — never the runner's capitalized slug
        // ("Googlecalendar", "Granola_mcp": staging 09-03).
        const displayName = resolveIntegrationDisplayName(
          authToolkit,
          request.displayName
        );
        // Multi-account (design §7): the wire zod schema already validates
        // accountHint/mode (#2798), but the hand-written ComposioAuthRequest
        // contract in @vestra/agent-sdk hasn't caught up — cast locally so this
        // reads without a shared-package edit; drop once the upstream type gains
        // the fields.
        const { accountHint, mode } = request as typeof request & {
          accountHint?: string;
          mode?: "add";
        };
        taskStore.getState()._mutateTasks((tasks) => {
          const task = tasks.get(taskId);
          if (!task) return;
          stampPending(task, {
            ...base,
            type: "auth",
            toolkit: authToolkit,
            displayName,
            authUrl: request.authUrl,
            ...(accountHint ? { accountHint } : {}),
            ...(mode === "add" ? { mode: "add" as const } : {}),
          });
          // composio_auth renders inline in the message stream (the AuthCard
          // on the triggering tool_use block) — not in the bottom command-
          // center. Stamp meta-auth + integration metadata on the tool block
          // so AuthCard reads the right toolkit/displayName even when the
          // raw tool name doesn't encode them (e.g. MANAGE_CONNECTIONS).
          if (task.streamingMessage && base.toolUseId) {
            for (const block of task.streamingMessage.content) {
              if (
                block.type === "tool_use" &&
                (block as ToolUseBlock).toolUseId === base.toolUseId
              ) {
                const formattedName = displayName;
                const existing = (block as ToolUseBlock).display;
                (block as ToolUseBlock).display = {
                  displayText:
                    existing?.displayText || `Connect ${formattedName}`,
                  ...existing,
                  category: "meta-auth",
                  integration: authToolkit,
                  integrationName: formattedName,
                };
                break;
              }
            }
          }
        });
        return;
      }

      case "tool_permission": {
        taskStore.getState()._mutateTasks((tasks) => {
          const task = tasks.get(taskId);
          if (!task) return;
          stampPending(task, {
            ...base,
            type: "permission",
            toolName: request.toolName,
            toolInput: request.toolInput,
            // Sandbox-computed plain-language headline — absent on old bundles.
            ...(request.summary && { summary: request.summary }),
            // Toolkit slug for the brand icon — absent ⇒ generic glyph.
            ...(request.appSlug && { appSlug: request.appSlug }),
            // SDK-supplied "Always allow" rules — absent ⇒ button hidden.
            ...(request.suggestions && { suggestions: request.suggestions }),
            // Rich-card fields (describePermissionAsk) — absent ⇒ summary-only card.
            ...(request.details && { details: request.details }),
            ...(request.actionLabel && { actionLabel: request.actionLabel }),
            ...(request.reason && { reason: request.reason }),
          });
          if (task.streamingMessage) {
            for (const block of task.streamingMessage.content) {
              if (
                block.type === "tool_use" &&
                (block as ToolUseBlock).toolUseId === base.toolUseId
              ) {
                (block as ToolUseBlock).permissionPending = true;
                break;
              }
            }
          }
        });
        return;
      }

      case "email_send_approval": {
        // Agent email: the SERVER's own card for an email it stored; the server sends it on the yes. Drawn by the
        // permission card from the server's words (Send email / Not now). No tool input, no Always allow.
        taskStore.getState()._mutateTasks((tasks) => {
          const task = tasks.get(taskId);
          if (!task) return;
          stampPending(task, {
            ...base,
            type: "permission",
            toolName: "email_send",
            toolInput: {},
            summary: request.summary,
            details: request.details,
            actionLabel: request.actionLabel,
            reason: request.reason,
          });
        });
        return;
      }

      case "exit_plan": {
        taskStore.getState()._mutateTasks((tasks) => {
          const task = tasks.get(taskId);
          if (!task) return;
          stampPending(task, {
            ...base,
            type: "plan",
            plan: request.plan,
          });
        });
        return;
      }

      case "credit_approval": {
        taskStore.getState()._mutateTasks((tasks) => {
          const task = tasks.get(taskId);
          if (!task) return;
          stampPending(task, {
            ...base,
            type: "credit",
            toolName: request.toolName,
            estimatedCostUsd: request.estimatedCostUsd,
            balanceUsd: request.balanceUsd,
          });
        });
        return;
      }

      case "hands_approval": {
        // OS Hands P2.5a — the hands gate's held consent ask (server-gate minted,
        // main-agent lane). Stamp only the gate's projected fields; the card
        // (HandsApprovalInput) derives all copy from them. firstEngage names no
        // app, so the per-action fields are simply absent on that variant.
        taskStore.getState()._mutateTasks((tasks) => {
          const task = tasks.get(taskId);
          if (!task) return;
          stampPending(task, {
            ...base,
            type: "hands",
            nonce: request.nonce,
            ...(request.lane !== undefined && { lane: request.lane }),
            ...(request.firstEngage !== undefined && {
              firstEngage: request.firstEngage,
            }),
            ...(request.app !== undefined && { app: request.app }),
            ...(request.url !== undefined && { url: request.url }),
            ...(request.capability !== undefined && {
              capability: request.capability,
            }),
            ...(request.tool !== undefined && { tool: request.tool }),
            ...(request.mode !== undefined && { mode: request.mode }),
            ...(request.label !== undefined && { label: request.label }),
            ...(request.tier !== undefined && { tier: request.tier }),
          });
        });
        return;
      }

      case "plan_limit": {
        // Engines T5 — the server paused the task on the person's own used-up
        // plan (the relay forwards AGENT_END only after this, so the turn end
        // sees the card and draws no failure). The request is the whole card.
        taskStore.getState()._mutateTasks((tasks) => {
          const task = tasks.get(taskId);
          if (!task) return;
          stampPending(task, { ...base, type: "plan_limit", request });
        });
        return;
      }

      case "custom": {
        taskStore.getState()._mutateTasks((tasks) => {
          const task = tasks.get(taskId);
          if (!task) return;
          const custom: PendingCustomInteraction = {
            ...base,
            type: "custom",
            renderer: request.renderer,
            config: request.config,
            ...(request.title !== undefined && { title: request.title }),
            ...(request.description !== undefined && {
              description: request.description,
            }),
            ...(request.submitLabel !== undefined && {
              submitLabel: request.submitLabel,
            }),
            ...(request.allowSkip !== undefined && {
              allowSkip: request.allowSkip,
            }),
            ...(request.skipLabel !== undefined && {
              skipLabel: request.skipLabel,
            }),
          };
          stampPending(task, custom);
        });
        return;
      }
    }
  };

  socket.on(EVENTS.INTERACTION_REQUIRED, handleInteractionRequired);

  return () => {
    socket.off(EVENTS.INTERACTION_REQUIRED, handleInteractionRequired);
  };
}
